import React from 'react'
import { Box, Stack, Typography } from '@mui/material'

import { INTERAX_COLORS } from '../../constants/colors'
import { useTranslation } from '../hooks/useIntractionTranslate'
import { AI_COACH_VOICE_PRESETS } from '../../../../pure-js/libs/aiCoachVoicePresets'
import { CoachChoiceChip } from './CoachChoiceChip'
import { COACH_FREQUENCY_OPTIONS } from './coachPreferenceOptions'
import { coachCardWithPaddingSx } from './coachSurfaceStyles'
import { useCoachPreferences } from './useCoachPreferences'

/**
 * Per-user Aria preferences: how often she may reach out, and the voice she uses in the 1:1 coach.
 * Group activities always use the default voice.
 */
export const CoachPreferencesSection: React.FC = () => {
  const { t } = useTranslation()
  const { contactsPerMonth, voicePresetId, isSaving, saveFailed, setContactFrequency, setVoicePreset } =
    useCoachPreferences()

  return (
    <Stack spacing={2.5} sx={coachCardWithPaddingSx}>
      <Box>
        <Typography sx={{ color: INTERAX_COLORS.white, fontWeight: 700 }}>{t('settings.coach.title')}</Typography>
        <Typography sx={{ color: INTERAX_COLORS.thunderGrey, fontSize: 13, mt: 0.5 }}>
          {t('settings.coach.subtitle')}
        </Typography>
      </Box>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap role="radiogroup">
        {COACH_FREQUENCY_OPTIONS.map((option) => (
          <CoachChoiceChip
            key={option.value}
            selected={contactsPerMonth === option.value}
            disabled={isSaving}
            onSelect={() => setContactFrequency(option.value)}
            label={t(option.labelKey)}
          />
        ))}
      </Stack>

      <Box>
        <Typography sx={{ color: INTERAX_COLORS.white, fontWeight: 700 }}>{t('settings.coach.voice.title')}</Typography>
        <Typography sx={{ color: INTERAX_COLORS.thunderGrey, fontSize: 13, mt: 0.5 }}>
          {t('settings.coach.voice.subtitle')}
        </Typography>
      </Box>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap role="radiogroup">
        {AI_COACH_VOICE_PRESETS.map((preset) => (
          <CoachChoiceChip
            key={preset.id}
            selected={voicePresetId === preset.id}
            disabled={isSaving}
            onSelect={() => setVoicePreset(preset.id)}
            label={t(preset.labelKey)}
            description={t(preset.descriptionKey)}
          />
        ))}
      </Stack>

      {saveFailed && (
        <Typography sx={{ color: INTERAX_COLORS.redError, fontSize: 13 }}>{t('settings.coach.saveFailed')}</Typography>
      )}
    </Stack>
  )
}

export default CoachPreferencesSection
